/**
 * Stability Detection Hook
 * 
 * Detects camera stability by comparing consecutive frames using frame differencing.
 * When the user holds the camera steady, the difference between frames is minimal.
 * 
 * Algorithm:
 * 1. Downsample video frame to 160x120 for performance
 * 2. Sample every 4th pixel (further speedup)
 * 3. Compare red channel with previous frame
 * 4. Calculate mean absolute difference
 * 5. Lower difference = more stable
 * 
 * Runs at 10fps for responsive stability detection.
 * 
 * Stability is confirmed when motion score stays below threshold
 * for a configurable duration (default: 500ms = 5 frames at 10fps).
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// =============================================================================
// TYPES
// =============================================================================

export interface StabilityDetectionResult {
  /** Whether the camera is currently stable */
  isStable: boolean;
  /** Number of consecutive stable frames */
  stableFrameCount: number;
  /** Current motion score (lower = more stable) */
  motionScore: number;
  /** Duration stable in ms */
  stableDurationMs: number;
  /** Human-readable guidance text */
  guidance: string;
}

export interface StabilityDetectionConfig {
  /** Motion score threshold below which is considered stable (default: 5) */
  motionThreshold?: number;
  /** Number of stable frames required to confirm stability (default: 5) */
  stableFramesRequired?: number;
  /** Width to downsample to for processing (default: 160) */
  processingWidth?: number;
  /** Height to downsample to for processing (default: 120) */
  processingHeight?: number;
  /** Processing interval in ms (default: 100 = 10fps) */
  intervalMs?: number;
  /** Pixel sampling rate - sample every Nth pixel (default: 4) */
  sampleRate?: number;
  /** Whether detection is enabled (default: true) */
  enabled?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_CONFIG: Required<StabilityDetectionConfig> = {
  // v2: Relaxed thresholds for hand-held mobile devices
  // Previous: motionThreshold=5 was too strict, users couldn't hold still enough
  // Motion score is mean absolute pixel diff (0-255), 8 allows natural hand tremor
  motionThreshold: 8,
  // Reduced from 5 to 3 frames for faster capture response
  stableFramesRequired: 3,
  processingWidth: 160,
  processingHeight: 120,
  intervalMs: 100,
  sampleRate: 4,
  enabled: true,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Extract sampled red channel values from video frame
 * Samples every Nth pixel for performance
 */
function extractSampledChannel(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  sampleRate: number
): Uint8Array | null {
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return null;
  
  // Set canvas to processing size
  canvas.width = width;
  canvas.height = height;
  
  // Draw downsampled video frame
  ctx.drawImage(video, 0, 0, width, height);
  
  // Get image data
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Sample every Nth pixel, extract red channel only
  const sampledCount = Math.ceil((width * height) / sampleRate);
  const sampled = new Uint8Array(sampledCount);
  
  let sampleIdx = 0;
  for (let i = 0; i < data.length; i += 4 * sampleRate) {
    if (sampleIdx < sampledCount) {
      sampled[sampleIdx++] = data[i]; // Red channel
    }
  }
  
  return sampled;
}

/**
 * Calculate mean absolute difference between two frames
 */
function calculateFrameDifference(
  prev: Uint8Array,
  curr: Uint8Array
): number {
  if (prev.length !== curr.length || prev.length === 0) return 255;
  
  let totalDiff = 0;
  for (let i = 0; i < prev.length; i++) {
    totalDiff += Math.abs(prev[i] - curr[i]);
  }
  
  return totalDiff / prev.length;
}

/**
 * Get guidance text based on stability state
 */
function getGuidanceText(isStable: boolean, motionScore: number): string {
  if (isStable) {
    return 'Holding steady...';
  }
  if (motionScore > 20) {
    return 'Too much movement';
  }
  if (motionScore > 10) {
    return 'Hold still...';
  }
  return 'Almost there...';
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for real-time camera stability detection using frame differencing
 * 
 * @param videoRef - Reference to the video element
 * @param config - Configuration options
 * @returns Stability detection result
 * 
 * @example
 * ```tsx
 * const videoRef = useRef<HTMLVideoElement>(null);
 * const { isStable, stableFrameCount, motionScore } = useStabilityDetection(videoRef);
 * 
 * // Auto-capture when stable for 500ms
 * useEffect(() => {
 *   if (isStable && stableFrameCount >= 5) {
 *     captureImage();
 *   }
 * }, [isStable, stableFrameCount]);
 * ```
 */
export function useStabilityDetection(
  videoRef: React.RefObject<HTMLVideoElement>,
  config: StabilityDetectionConfig = {}
): StabilityDetectionResult {
  const {
    motionThreshold,
    stableFramesRequired,
    processingWidth,
    processingHeight,
    intervalMs,
    sampleRate,
    enabled,
  } = { ...DEFAULT_CONFIG, ...config };
  
  // State
  const [isStable, setIsStable] = useState(false);
  const [stableFrameCount, setStableFrameCount] = useState(0);
  const [motionScore, setMotionScore] = useState(255);
  const [stableDurationMs, setStableDurationMs] = useState(0);
  
  // Refs
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const prevFrameRef = useRef<Uint8Array | null>(null);
  const intervalRef = useRef<number | null>(null);
  const stableStartRef = useRef<number | null>(null);
  
  // Cleanup interval
  const cleanup = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    prevFrameRef.current = null;
    stableStartRef.current = null;
  }, []);
  
  // Process frame
  const processFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    
    // Create canvas lazily
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    
    // Extract current frame
    const currFrame = extractSampledChannel(
      video,
      canvasRef.current,
      processingWidth,
      processingHeight,
      sampleRate
    );
    
    if (!currFrame) return;
    
    // If we have a previous frame, calculate difference
    if (prevFrameRef.current) {
      const diff = calculateFrameDifference(prevFrameRef.current, currFrame);
      setMotionScore(diff);
      
      if (diff < motionThreshold) {
        // Frame is stable
        setStableFrameCount(prev => {
          const newCount = prev + 1;
          const nowStable = newCount >= stableFramesRequired;
          setIsStable(nowStable);
          
          // Track stable duration
          if (nowStable) {
            if (!stableStartRef.current) {
              stableStartRef.current = Date.now();
            }
            setStableDurationMs(Date.now() - stableStartRef.current);
          }
          
          return newCount;
        });
      } else {
        // Frame is moving - reset
        setStableFrameCount(0);
        setIsStable(false);
        setStableDurationMs(0);
        stableStartRef.current = null;
      }
    }
    
    // Store current frame for next comparison
    prevFrameRef.current = currFrame;
  }, [
    videoRef,
    processingWidth,
    processingHeight,
    sampleRate,
    motionThreshold,
    stableFramesRequired,
  ]);
  
  // Reset function (exposed for manual reset)
  const reset = useCallback(() => {
    setStableFrameCount(0);
    setIsStable(false);
    setMotionScore(255);
    setStableDurationMs(0);
    prevFrameRef.current = null;
    stableStartRef.current = null;
  }, []);
  
  // Start/stop detection based on enabled flag
  useEffect(() => {
    if (!enabled) {
      cleanup();
      reset();
      return;
    }
    
    // Start interval
    intervalRef.current = window.setInterval(processFrame, intervalMs);
    
    return cleanup;
  }, [enabled, intervalMs, processFrame, cleanup, reset]);
  
  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);
  
  return {
    isStable,
    stableFrameCount,
    motionScore,
    stableDurationMs,
    guidance: getGuidanceText(isStable, motionScore),
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

// StabilityDetectionConfig is already exported at definition (line 39)
export { DEFAULT_CONFIG as STABILITY_DETECTION_DEFAULTS };
